import type { CategoryStorage, ConsentStatus, DataCategory, NoticeGroup, Processor, StorageEntry } from "./data";

export type ApiCategory = {
  id: string;
  code: string;
  name: string;
  groupName?: string;
  example?: string;
  sensitive?: boolean;
  usedBy?: string[];
  languages?: { languageCode?: string; name?: string }[];
  stores?: { systemName?: string; databaseName?: string; storageTable?: string; field?: string; description?: string }[];
};

export type ApiProcessor = {
  id: string;
  code: string;
  name: string;
  role?: string;
  location?: string;
  crossBorder?: boolean;
  contract?: string;
  usedBy?: string[];
};

export type ApiPurpose = {
  id: string;
  code: string;
  name: string;
  basis?: string;
  retention?: string;
  pwd?: string;
  mandatory?: boolean;
  categories?: { categoryCode?: string }[];
  processors?: { processorCode?: string }[];
  principals?: { principal?: string }[];
  mappedNotices?: string[];
  languages?: { languageCode?: string; name?: string }[];
};

export type ApiNotice = {
  id: string;
  code: string;
  name?: string;
  description?: string;
  owner?: string;
  noticeVersion: string;
  status?: string;
  effectiveOn?: string;
  languages?: { languageCode?: string; title?: string; body?: string }[];
  purposes?: { purposeId?: string; code?: string }[];
  tools?: { toolId?: string }[];
};

export type ApiConsent = {
  id?: string | number;
  name?: string;
  description?: string;
  purpose?: string;
  status?: string;
  noticeId?: string;
  toolId?: string;
  language?: string;
  category?: string;
  noticeVersion?: string;
  expiry?: string;
  dataCategories?: string[];
  channel?: string;
  captureMethod?: string;
  withdrawnAt?: string;
  createdAt?: string;
  requestee?: { userId?: string };
  originatorDetails?: { userId?: string };
  artifactDetails?: { approvedTimestamp?: string };
  details?: { data?: string };
};

export type ConsentRecord = {
  id: string;
  principal: string;
  userId: string;
  principalType?: string;
  principalSource?: string;
  applicationId: string;
  category: string;
  purpose: string;
  purposeId: string;
  noticeId: string;
  noticeName: string;
  notice: string;
  language: string;
  status: ConsentStatus | string;
  granted: string;
  withdrawn: string;
  retainedUntil?: string;
  touchpoint: string;
  capture: string;
  pii: string[];
  processors?: string[];
  guardian?: boolean;
  warning?: boolean;
};

const consentStatusLabels: Record<string, string> = {
  GRANTED: "Active",
  APPROVED: "Active",
  REVOKED: "Withdrawn",
  REJECTED: "Declined",
};

function filled(value?: string) {
  const text = value?.trim();
  return text || "—";
}

export function captureConsentPayloads(input: {
  realmId: string;
  operatorUserId: string;
  userId: string;
  principalType?: string;
  principalSource?: string;
  applicationId: string;
  applicationName: string;
  noticeName: string;
  noticeRecordId?: string;
  noticeVersion?: string;
  language: string;
  grantedAt: string;
  channel?: string;
  choices: { purposeId: string; purposeName: string; category?: string; categories?: string[]; processors?: string[]; retentionDays?: string; granted: boolean }[];
}) {
  const principal = { realmId: input.realmId, userId: input.userId };
  const noticeId = input.noticeRecordId && /^\d+$/.test(input.noticeRecordId) ? input.noticeRecordId : undefined;
  const channel = (input.channel || input.applicationName || input.applicationId).trim();
  return input.choices.filter((choice) => choice.granted).map((choice) => {
    const expiry = consentExpiry(input.grantedAt, choice.retentionDays);
    return {
    name: input.noticeName,
    description: choice.purposeName,
    purpose: choice.purposeId,
    status: "GRANTED",
    type: "DPDPA_PURPOSE_CONSENT",
    details: {
      consentType: "TEXT",
      data: JSON.stringify({
        purposeName: choice.purposeName,
        categories: choice.categories ?? [],
        processors: choice.processors ?? [],
        ...(input.principalType ? { principalType: input.principalType } : {}),
        ...(input.principalSource ? { principalSource: input.principalSource } : {}),
      }),
    },
    originatorDetails: principal,
    requestor: { realmId: input.realmId, userId: input.operatorUserId },
    requestee: principal,
    ...(noticeId ? { noticeId } : {}),
    ...(choice.category ? { category: choice.category } : {}),
    ...(input.noticeVersion ? { noticeVersion: input.noticeVersion } : {}),
    language: input.language,
    ...(channel ? { channel } : {}),
    captureMethod: "Checkbox",
    ...(expiry ? { expiry } : {}),
    artifactDetails: { approvedTimestamp: input.grantedAt },
  };
  });
}

function consentSnapshot(row: ApiConsent) {
  const empty = { purposeName: "", categories: [] as string[], processors: [] as string[], principalType: "", principalSource: "" };
  const raw = row.details?.data?.trim() ?? "";
  if (!raw.startsWith("{")) return empty;
  try {
    const parsed = JSON.parse(raw) as { purposeName?: unknown; categories?: unknown; processors?: unknown; principalType?: unknown; principalSource?: unknown };
    const names = (value: unknown) => Array.isArray(value) ? value.filter((item): item is string => typeof item === "string" && item.trim() !== "") : [];
    const text = (value: unknown) => typeof value === "string" ? value.trim() : "";
    return {
      purposeName: text(parsed.purposeName),
      categories: names(parsed.categories),
      processors: names(parsed.processors),
      principalType: text(parsed.principalType),
      principalSource: text(parsed.principalSource),
    };
  } catch {
    return empty;
  }
}

export function toConsentRecord(row: ApiConsent): ConsentRecord {
  const userId = row.requestee?.userId || row.originatorDetails?.userId || "";
  const granted = row.artifactDetails?.approvedTimestamp || row.createdAt || "";
  const purposeCode = row.purpose || "";
  const snapshot = consentSnapshot(row);
  const purposeName = snapshot.purposeName || row.description?.trim();
  const categories = snapshot.categories;
  return {
    id: String(row.id ?? ""),
    principal: filled(userId),
    userId: filled(userId),
    ...(snapshot.principalType ? { principalType: snapshot.principalType } : {}),
    ...(snapshot.principalSource ? { principalSource: snapshot.principalSource } : {}),
    applicationId: filled(row.toolId),
    category: filled(row.category),
    purpose: filled(purposeName && purposeName !== purposeCode ? purposeName : purposeCode || row.name),
    purposeId: filled(purposeCode),
    noticeId: filled(row.noticeId),
    noticeName: filled(row.name || row.noticeId),
    notice: filled(row.noticeVersion),
    language: filled(row.language),
    status: consentStatusLabels[row.status ?? ""] ?? filled(row.status),
    granted: granted ? formatRetention(granted) : "—",
    withdrawn: row.withdrawnAt ? formatRetention(row.withdrawnAt) : "—",
    retainedUntil: row.expiry ? formatRetention(row.expiry) : "—",
    touchpoint: filled(row.channel),
    capture: filled(row.captureMethod),
    pii: categories.length ? categories : (row.dataCategories ?? []),
    processors: snapshot.processors,
  };
}

function consentActivity(row: ApiConsent) {
  return [row.withdrawnAt, row.artifactDetails?.approvedTimestamp]
    .map((value) => Date.parse(value ?? ""))
    .filter((value) => !Number.isNaN(value))
    .reduce((latest, value) => Math.max(latest, value), 0);
}

export function currentConsentRows(rows: ApiConsent[]) {
  const kept = new Map<string, ApiConsent>();
  for (const row of rows) {
    if (row.status !== "GRANTED" && row.status !== "APPROVED" && row.status !== "REVOKED") continue;
    const userId = row.requestee?.userId || row.originatorDetails?.userId || "";
    const key = `${userId}\n${row.toolId || ""}\n${row.purpose || ""}`;
    const existing = kept.get(key);
    if (!existing || Number(row.id) >= Number(existing.id)) kept.set(key, row);
  }
  return [...kept.values()].sort((left, right) => {
    const leftTime = consentActivity(left);
    const rightTime = consentActivity(right);
    if (leftTime && rightTime && leftTime !== rightTime) return rightTime - leftTime;
    return Number(right.id) - Number(left.id);
  }).map(toConsentRecord);
}

const statusLabels: Record<string, string> = {
  DRAFT: "Draft",
  PUBLISHED: "Published",
  SUPERSEDED: "Superseded",
};

export function nextCode(prefix: string, ids: string[]) {
  const max = ids.reduce((highest, id) => Math.max(highest, Number(id.split("-")[1] ?? 0)), 0);
  return `${prefix}-${String(max + 1).padStart(2, "0")}`;
}

export function displayStatus(status?: string) {
  if (!status) return "Draft";
  return statusLabels[status] ?? status;
}

export function yesNo(value?: boolean) {
  return value ? "Yes" : "No";
}

export function toRetentionTimestamp(localValue: string) {
  const parsed = new Date(localValue);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString();
}

export function storedChannel(toolName: string, toolId: string) {
  const name = toolName.trim();
  const id = toolId.trim();
  return (name || id).slice(0, 40);
}

export function shownChannel(channel?: string, toolId?: string) {
  const name = (channel || "").trim();
  const id = (toolId || "").trim();
  if (!name || name === "—") return id || "—";
  if (!id || name.includes(id)) return name;
  return `${name} · ${id}`;
}

export function formatRetention(value: string) {
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return value;
  return new Intl.DateTimeFormat("en-IN", { dateStyle: "medium", timeStyle: "short" }).format(parsed);
}

export function formatPurposeRetention(value: string) {
  const days = value.trim();
  if (/^\d+$/.test(days)) return `${days} day${days === "1" ? "" : "s"}`;
  return formatRetention(value);
}

export const catalogLanguages = [
  { code: "EN", label: "English" },
  { code: "HI", label: "हिन्दी" },
  { code: "KN", label: "ಕನ್ನಡ" },
  { code: "TA", label: "தமிழ்" },
  { code: "TE", label: "తెలుగు" },
] as const;

export type LanguageCode = (typeof catalogLanguages)[number]["code"];
export type LanguageNames = Partial<Record<LanguageCode, string>>;

export function languageCode(value: string): LanguageCode {
  const match = catalogLanguages.find((language) => language.code === value || language.label === value);
  return match?.code ?? "EN";
}

export function languageLabel(code: string) {
  return catalogLanguages.find((language) => language.code === code)?.label ?? code;
}

export function translatedName(english: string, names: LanguageNames | undefined, code: string) {
  if (code === "EN") return english;
  const translated = names?.[code as LanguageCode]?.trim();
  return translated || english;
}

export function namesFromLanguages(languages?: { languageCode?: string; name?: string }[]) {
  const names: LanguageNames = {};
  languages?.forEach((language) => {
    const code = language.languageCode;
    const name = language.name?.trim();
    if (code && code !== "EN" && name && catalogLanguages.some((item) => item.code === code)) names[code as LanguageCode] = name;
  });
  return names;
}

export function emptyStorage(): StorageEntry {
  return { kind: "document", documentUrl: "", query: "", tableName: "", description: "", dataSourceId: "", testValues: {}, testedOk: false, testError: "" };
}

export function parseQueryPlaceholders(query: string): string[] {
  const names: string[] = [];
  const seen = new Set<string>();
  const matcher = /\{\{\s*([A-Za-z_][A-Za-z0-9_]*)\s*\}\}/g;
  let match: RegExpExecArray | null;
  while ((match = matcher.exec(query)) !== null) {
    if (seen.has(match[1])) continue;
    seen.add(match[1]);
    names.push(match[1]);
  }
  return names;
}

export function hasUserIdPlaceholder(query: string) {
  return parseQueryPlaceholders(query).includes("userId");
}

export function queryResultTable(result: unknown): { columns: string[]; rows: Record<string, unknown>[] } {
  if (!result || typeof result !== "object") return { columns: [], rows: [] };
  const data = result as { columns?: unknown; rows?: unknown };
  const rows = Array.isArray(data.rows)
    ? data.rows.filter((row): row is Record<string, unknown> => !!row && typeof row === "object" && !Array.isArray(row))
    : [];
  const columnsFromMeta = Array.isArray(data.columns)
    ? data.columns.flatMap((column) => {
      if (typeof column === "string" && column.trim()) return [column.trim()];
      if (column && typeof column === "object" && "name" in column) {
        const name = String((column as { name: unknown }).name ?? "").trim();
        return name ? [name] : [];
      }
      return [];
    })
    : [];
  const columns = columnsFromMeta.length ? columnsFromMeta : (rows[0] ? Object.keys(rows[0]) : []);
  return { columns, rows };
}

export function storageEntriesFromCategory(stores?: CategoryStorage[]): StorageEntry[] {
  return (stores ?? []).map((store) => {
    if (store.systemName === "document") {
      return { ...emptyStorage(), kind: "document", documentUrl: store.databaseName };
    }
    return {
      ...emptyStorage(),
      kind: "database",
      dataSourceId: store.databaseName && store.databaseName !== "-" ? store.databaseName : "",
      tableName: store.storageTable === "-" ? "" : store.storageTable,
      description: store.description ?? "",
      query: store.field === "-" ? "" : store.field,
    };
  });
}

export function storageRows(stores: StorageEntry[]): CategoryStorage[] | null {
  const rows: CategoryStorage[] = [];
  for (const store of stores) {
    const documentUrl = store.documentUrl.trim();
    const query = store.query.trim();
    const tableName = store.tableName.trim();
    const description = store.description.trim();
    const dataSourceId = store.dataSourceId.trim();
    if (store.kind === "database") {
      if (!query && !tableName && !description && !dataSourceId) continue;
      if (!query || !tableName || !description || !dataSourceId || !hasUserIdPlaceholder(query)) return null;
      rows.push({ systemName: "database", databaseName: dataSourceId, storageTable: tableName, field: query, description });
      continue;
    }
    if (!documentUrl) continue;
    rows.push({ systemName: "document", databaseName: documentUrl, storageTable: "-", field: "-" });
  }
  return rows;
}

export function databaseStoresTested(stores: StorageEntry[]) {
  return stores.every((store) => {
    if (store.kind !== "database") return true;
    const empty = !store.query.trim() && !store.tableName.trim() && !store.description.trim() && !store.dataSourceId.trim();
    return empty || (store.testedOk && hasUserIdPlaceholder(store.query));
  });
}

export function storagePlace(store: CategoryStorage) {
  if (store.systemName === "document") return { title: "Document", detail: store.databaseName };
  if (store.systemName === "database") return { title: "Database", detail: [store.databaseName, store.storageTable, store.description, store.field].filter((part) => part && part !== "-").join(" · ") };
  return { title: store.systemName, detail: [store.databaseName, store.storageTable, store.field, store.description].filter((part) => part && part !== "-").join(".") };
}

export function languageRows(names?: LanguageNames) {
  return catalogLanguages.flatMap((language) => {
    if (language.code === "EN") return [];
    const name = names?.[language.code]?.trim();
    return name ? [{ languageCode: language.code, name }] : [];
  });
}

export function emptyLanguageNames(): Record<Exclude<LanguageCode, "EN">, string> {
  return { HI: "", KN: "", TA: "", TE: "" };
}

export function emptyNoticeTexts(): Record<Exclude<LanguageCode, "EN">, { title: string; body: string }> {
  return { HI: { title: "", body: "" }, KN: { title: "", body: "" }, TA: { title: "", body: "" }, TE: { title: "", body: "" } };
}

export function noticeLanguageRows(name: string, description: string, texts: Record<string, { title: string; body: string }>) {
  const rows = [{ languageCode: "EN", title: name, body: description }];
  catalogLanguages.forEach((language) => {
    if (language.code === "EN") return;
    const text = texts[language.code];
    const body = text?.body.trim();
    if (!body) return;
    rows.push({ languageCode: language.code, title: text.title.trim() || name, body });
  });
  return rows;
}

export function noticeForLanguage(version: { texts?: { language: string; title: string; body: string }[] } | undefined, requested: string, englishName: string) {
  const code = languageCode(requested);
  const text = code === "EN" ? undefined : version?.texts?.find((item) => item.language === code && item.body.trim());
  if (text) return { code, title: text.title.trim() || englishName };
  return { code: "EN" as const, title: englishName };
}

export function purposeInLanguage<T extends { name: string; pii: string[]; names?: LanguageNames }>(purpose: T, categories: { name: string; names?: LanguageNames }[], code: string): T {
  const categoryNames = new Map(categories.map((category) => [category.name, category.names]));
  return { ...purpose, name: translatedName(purpose.name, purpose.names, code), pii: purpose.pii.map((item) => translatedName(item, categoryNames.get(item), code)) };
}

export const userPortalHomeUrl = "https://user-portal.trafyn.info/home";

export const captureCopy: Record<LanguageCode, { eyebrow: string; title: string; instruction: string; read: string; continue: string; accept: string; agree: string; cancel: string; saving: string; saved: string; given: string; declined: string; basis: string; retention: string; privacy: string; withdraw: string; categories: string; collecting: string; processors: string; portal: string }> = {
  EN: { eyebrow: "Your privacy choice", title: "Confirm how we use your data", instruction: "Please choose separately for each purpose. Nothing is selected for you.", read: "Read", continue: "Submit", accept: "I accept conditions", agree: "Agree", cancel: "Cancel", saving: "Saving", saved: "Your choices are saved", given: "Consent given", declined: "Consent not given", basis: "Why we need it", retention: "How long we keep it", privacy: "Privacy contact:", withdraw: "If you want to withdraw consent, please check the ", categories: "What we collect", collecting: "We are collecting", processors: "Shared with", portal: "user portal" },
  HI: { eyebrow: "आपकी गोपनीयता की पसंद", title: "पुष्टि करें कि हम आपके डेटा का उपयोग कैसे करते हैं", instruction: "प्रत्येक उद्देश्य के लिए अलग से चुनें। आपके लिए कुछ भी पहले से नहीं चुना गया है।", read: "पढ़ें", continue: "जमा करें", accept: "मैं शर्तें स्वीकार करता/करती हूँ", agree: "सहमत", cancel: "रद्द करें", saving: "सहेजा जा रहा है", saved: "आपकी पसंद सहेज ली गई है", given: "सहमति दी गई", declined: "सहमति नहीं दी गई", basis: "हमें इसकी जरूरत क्यों है", retention: "हम इसे कितने दिन रखते हैं", privacy: "गोपनीयता संपर्क:", withdraw: "सहमति वापस लेने के लिए कृपया ", categories: "हम क्या इकट्ठा करते हैं", collecting: "हम यह इकट्ठा कर रहे हैं", processors: "किसके साथ साझा किया जाता है", portal: "उपयोगकर्ता पोर्टल" },
  KN: { eyebrow: "ನಿಮ್ಮ ಗೌಪ್ಯತೆ ಆಯ್ಕೆ", title: "ನಿಮ್ಮ ಡೇಟಾವನ್ನು ನಾವು ಹೇಗೆ ಬಳಸುತ್ತೇವೆ ಎಂಬುದನ್ನು ದೃಢೀಕರಿಸಿ", instruction: "ಪ್ರತಿ ಉದ್ದೇಶಕ್ಕೂ ಪ್ರತ್ಯೇಕವಾಗಿ ಆಯ್ಕೆಮಾಡಿ. ನಿಮಗಾಗಿ ಏನೂ ಮುಂಚಿತವಾಗಿ ಆಯ್ಕೆಯಾಗಿಲ್ಲ.", read: "ಓದಿ", continue: "ಸಲ್ಲಿಸಿ", accept: "ನಾನು ನಿಯಮಗಳನ್ನು ಒಪ್ಪುತ್ತೇನೆ", agree: "ಒಪ್ಪುತ್ತೇನೆ", cancel: "ರದ್ದುಮಾಡಿ", saving: "ಉಳಿಸಲಾಗುತ್ತಿದೆ", saved: "ನಿಮ್ಮ ಆಯ್ಕೆಗಳನ್ನು ಉಳಿಸಲಾಗಿದೆ", given: "ಸಮ್ಮತಿ ನೀಡಲಾಗಿದೆ", declined: "ಸಮ್ಮತಿ ನೀಡಲಾಗಿಲ್ಲ", basis: "ನಮಗೆ ಇದು ಏಕೆ ಬೇಕು", retention: "ನಾವು ಇದನ್ನು ಎಷ್ಟು ಕಾಲ ಇಡುತ್ತೇವೆ", privacy: "ಗೌಪ್ಯತೆ ಸಂಪರ್ಕ:", withdraw: "ಸಮ್ಮತಿಯನ್ನು ಹಿಂತೆಗೆದುಕೊಳ್ಳಲು ಬಯಸಿದರೆ ", categories: "ನಾವು ಏನು ಸಂಗ್ರಹಿಸುತ್ತೇವೆ", collecting: "ನಾವು ಇದನ್ನು ಸಂಗ್ರಹಿಸುತ್ತಿದ್ದೇವೆ", processors: "ಯಾರೊಂದಿಗೆ ಹಂಚಿಕೊಳ್ಳಲಾಗುತ್ತದೆ", portal: "ಬಳಕೆದಾರ ಪೋರ್ಟಲ್" },
  TA: { eyebrow: "உங்கள் தனியுரிமை தேர்வு", title: "உங்கள் தரவை நாங்கள் எப்படி பயன்படுத்துகிறோம் என்பதை உறுதிசெய்யுங்கள்", instruction: "ஒவ்வொரு நோக்கத்திற்கும் தனியாக தேர்வு செய்யுங்கள். உங்களுக்காக எதுவும் முன்கூட்டியே தேர்ந்தெடுக்கப்படவில்லை.", read: "படிக்க", continue: "சமர்ப்பிக்கவும்", accept: "நான் நிபந்தனைகளை ஏற்கிறேன்", agree: "ஏற்கிறேன்", cancel: "ரத்துசெய்", saving: "சேமிக்கப்படுகிறது", saved: "உங்கள் தேர்வுகள் சேமிக்கப்பட்டன", given: "ஒப்புதல் அளிக்கப்பட்டது", declined: "ஒப்புதல் அளிக்கப்படவில்லை", basis: "இது ஏன் தேவை", retention: "எவ்வளவு காலம் வைத்திருப்போம்", privacy: "தனியுரிமை தொடர்பு:", withdraw: "ஒப்புதலைத் திரும்பப் பெற விரும்பினால் ", categories: "நாங்கள் என்ன சேகரிக்கிறோம்", collecting: "நாங்கள் இதை சேகரிக்கிறோம்", processors: "யாருடன் பகிரப்படுகிறோம்", portal: "பயனர் போர்டல்" },
  TE: { eyebrow: "మీ గోప్యత ఎంపిక", title: "మీ డేటాను మేము ఎలా ఉపయోగిస్తామో నిర్ధారించండి", instruction: "ప్రతి ప్రయోజనానికి విడివిడిగా ఎంచుకోండి. మీ కోసం ఏదీ ముందే ఎంచుకోబడలేదు.", read: "చదవండి", continue: "సమర్పించండి", accept: "నేను నిబంధనలను అంగీకరిస్తున్నాను", agree: "అంగీకరించు", cancel: "రద్దు", saving: "సేవ్ అవుతోంది", saved: "మీ ఎంపికలు సేవ్ అయ్యాయి", given: "సమ్మతి ఇవ్వబడింది", declined: "సమ్మతి ఇవ్వబడలేదు", basis: "మాకు ఇది ఎందుకు అవసరం", retention: "మేము ఎంతకాలం ఉంచుతాము", privacy: "గోప్యత సంప్రదింపు:", withdraw: "సమ్మతిని ఉపసంహరించుకోవాలంటే ", categories: "మేము ఏమి సేకరిస్తాము", collecting: "మేము ఇది సంగ్రహిస్తున్నాము", processors: "ఎవరితో పంచుకున్నది", portal: "యూజర్ పోర్టల్" },
};

export type ConsentListFilters = {
  category: string;
  application: string;
  purpose: string;
  status: string;
  touchpoint: string;
  notice: string;
  text?: string;
};

export function consentQueryFilters(filters: ConsentListFilters) {
  const query: { property: string; operator: string; value: string | string[] }[] = [];
  if (filters.category) query.push({ property: "category", operator: "eq", value: filters.category });
  if (filters.application) query.push({ property: "toolId", operator: "eq", value: filters.application });
  if (filters.purpose) query.push({ property: "purpose", operator: "eq", value: filters.purpose });
  if (filters.status === "Active") query.push({ property: "status", operator: "or", value: ["GRANTED", "APPROVED"] });
  if (filters.status === "Withdrawn") query.push({ property: "status", operator: "eq", value: "REVOKED" });
  if (filters.status === "Declined") query.push({ property: "status", operator: "eq", value: "REJECTED" });
  if (filters.status === "Requested") query.push({ property: "status", operator: "eq", value: "REQUESTED" });
  if (filters.touchpoint) query.push({ property: "channel", operator: "eq", value: filters.touchpoint });
  if (filters.notice) query.push({ property: "name", operator: "eq", value: filters.notice });
  return query;
}

export function consentExpiry(grantedAt: string, retentionDays?: string) {
  const days = Number(retentionDays);
  const start = new Date(grantedAt);
  if (!Number.isInteger(days) || days < 1 || Number.isNaN(start.getTime())) return undefined;
  start.setDate(start.getDate() + days);
  const pad = (part: number) => String(part).padStart(2, "0");
  return `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}T${pad(start.getHours())}:${pad(start.getMinutes())}:${pad(start.getSeconds())}`;
}

export function toCategory(row: ApiCategory): DataCategory {
  return {
    id: row.code,
    name: row.name,
    group: row.groupName ?? "",
    example: row.example ?? "",
    sensitive: yesNo(row.sensitive),
    usedBy: row.usedBy ?? [],
    names: namesFromLanguages(row.languages),
    stores: (row.stores ?? []).flatMap((store) => store.systemName ? [{
      systemName: store.systemName,
      databaseName: store.databaseName ?? "",
      storageTable: store.storageTable ?? "",
      description: store.description ?? "",
      field: store.field ?? "",
    }] : []),
  };
}

export function toProcessor(row: ApiProcessor): Processor {
  return {
    id: row.code,
    name: row.name,
    role: row.role ?? "",
    location: row.location ?? "",
    crossBorder: yesNo(row.crossBorder),
    contract: row.contract ?? "",
    usedBy: row.usedBy ?? [],
  };
}

export function toPurpose(
  row: ApiPurpose,
  categoryNameByCode: Map<string, string>,
  processorNameByCode: Map<string, string>,
) {
  const categories = row.categories ?? [];
  const processors = row.processors ?? [];
  return {
    id: row.code,
    name: row.name,
    principal: (row.principals ?? []).map((item) => item.principal).filter(Boolean).join(", "),
    pii: categories.map((item) => categoryNameByCode.get(item.categoryCode ?? "") ?? item.categoryCode ?? "").filter(Boolean),
    basis: row.basis ?? "",
    recipients: processors.map((item) => processorNameByCode.get(item.processorCode ?? "") ?? item.processorCode ?? "").filter(Boolean).join(", "),
    retention: row.retention ?? "",
    pwd: row.pwd ?? "No",
    mandatory: Boolean(row.mandatory),
    reviewed: "",
    mappedNotices: row.mappedNotices ?? [],
    names: namesFromLanguages(row.languages),
  };
}

function versionRank(version: string) {
  const match = /^v(\d+)\.(\d+)$/.exec(version);
  if (!match) return 0;
  return Number(match[1]) * 1000 + Number(match[2]);
}

function firstFilled(rows: ApiNotice[], read: (row: ApiNotice) => string | undefined, fallback: string) {
  return rows.map(read).find((value) => value && value.trim()) ?? fallback;
}

export function groupNotices(
  rows: ApiNotice[],
  purposeCodeByRecordId: Map<string, string>,
): NoticeGroup[] {
  const byCode = new Map<string, ApiNotice[]>();
  rows.forEach((row) => {
    const versions = byCode.get(row.code) ?? [];
    versions.push(row);
    byCode.set(row.code, versions);
  });
  return [...byCode.entries()].map(([code, versions]) => {
    const ordered = [...versions].sort((left, right) => versionRank(right.noticeVersion) - versionRank(left.noticeVersion));
    return {
      id: code,
      name: firstFilled(ordered, (row) => row.name || row.languages?.find((language) => language.title)?.title, code),
      description: firstFilled(ordered, (row) => row.description, ""),
      owner: firstFilled(ordered, (row) => row.owner, ""),
      versions: ordered.map((row) => {
        const purposeLinks: NonNullable<ApiNotice["purposes"]> = row.purposes ?? [];
        const languageRows: NonNullable<ApiNotice["languages"]> = row.languages ?? [];
        const toolLinks: NonNullable<ApiNotice["tools"]> = row.tools ?? [];
        const ids = purposeLinks.map((purpose) => purpose.code || purposeCodeByRecordId.get(String(purpose.purposeId ?? "")) || String(purpose.purposeId ?? ""));
        return {
          recordId: String(row.id),
          version: row.noticeVersion,
          status: displayStatus(row.status),
          date: row.effectiveOn || "Not scheduled",
          languages: languageRows.map((language) => language.languageCode).filter((code): code is string => Boolean(code)),
          texts: languageRows.flatMap((language) => language.body?.trim() ? [{ language: language.languageCode ?? "", title: language.title ?? "", body: language.body.trim() }] : []),
          ids,
          links: ids.join(" · "),
          toolId: toolLinks.map((tool) => tool.toolId?.trim() ?? "").find((toolId) => toolId) ?? "",
        };
      }),
    };
  });
}
